import { Component, OnInit } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { OnlineStatusService, OnlineStatusType } from 'ngx-online-status';
import { ActiveToast, ToastrService } from 'ngx-toastr';
import { AuthService } from '../shared/auth.service';
import { LocalStorageService } from '../shared/local-storage.service';
import { OperatorUser } from '../shared/operator-user.model';
import { phoneNumberValidator } from '../sign-up/sign-up.component';
import { Tel } from '../tel-input/tel-input.component';
import { Subscription } from 'rxjs';
import type { DarkVariant } from '../shared/theme.service';
import { ThemeService } from '../shared/theme.service';

@Component({
    selector: 'app-profile',
    templateUrl: './profile.component.html',
    styleUrls: ['./profile.component.css'],
    standalone: false
})
export class ProfileComponent implements OnInit {

  // For profile updates
  profileForm = new FormGroup({
    firstName: new FormControl('', [Validators.required, Validators.pattern('^[A-z0-9 ]+$')]),
    lastName: new FormControl('', [Validators.required, Validators.pattern('^[A-z0-9 ]+$')]),
    email: new FormControl('', [Validators.required, Validators.email]),
    licenseNum: new FormControl('', [Validators.minLength(5), Validators.maxLength(8), Validators.pattern('^([a-zA-Z]|[0-9])+$')]),
    phoneNum: new FormControl(new Tel('', '', ''), [Validators.required, phoneNumberValidator()]) //Use the tel control
  });

  updateSubscription!: Subscription;
  activeToast: ActiveToast<any> | undefined;

  constructor(
    public dataSync: LocalStorageService,
    public router: Router,
    private auth: AuthService,
    private toastr: ToastrService,
    private onlineStatusService: OnlineStatusService,
    public theme: ThemeService) {

  }


  /**
   * Apply a dark palette from the picker. Selecting a style is also how the
   * user turns dark mode on, so switch modes unless a dark theme is already
   * showing (which it may be via "Device").
   * @param variant The dark palette to preview
   */
  previewVariant(variant: DarkVariant): void {
    this.theme.setDarkVariant(variant);
    if (!this.theme.isDark()) {
      this.theme.setMode('dark');
    }
  }

  ngOnInit(): void {
    if (this.auth.checkAccessToken().expired) {
      console.log("Access token expired, refreshing..");
      if (this.auth.checkRefreshToken().expired) {
        //Go to menu screen
        this.toastr.warning('Login to update your profile information', 'Sign-up session expired');
      } else {
        this.auth.postRefreshToken().subscribe((token: any) => {
          let user = this.dataSync.current_user();
          user.accessToken = token.accessToken;
          user.refreshToken = token.refreshToken;
          this.dataSync.current_user.set(user);
          this.dataSync.updateUser(false, true);
        });
      }
    }

    const user = this.dataSync.current_user();

    //In some instances, the user phone number may not be set
    this.profileForm.patchValue({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      licenseNum:user.licenseNumber,
      phoneNum: user.phoneNumber ? new Tel(user.phoneNumber.slice(0, 3), user.phoneNumber.slice(3, 6), user.phoneNumber.slice(6, 10)) : new Tel('', '', '')
    });

    this.updateSubscription = this.profileForm.valueChanges.subscribe(() => this.updateProfile());

  }


  /**
 * Get data from the form controls and send an update with {@link MenuComponent.sendProfileUpdate}
 * @returns 
 */
  updateProfile() {
    if (this.onlineStatusService.getStatus() == OnlineStatusType.OFFLINE) {
      this.toastr.warning('Internet connection is required to update profile information', 'Error');
      return;
    }

    if (this.auth.checkAccessToken().expired) {
      console.log("Access token expired, refreshing..");
      if (this.auth.checkRefreshToken().expired) {
        //Go to menu screen
        this.toastr.warning('Login to add and view your vessels', 'Sign-up session expired');
        this.router.navigate(['menu']);
      } else {
        this.auth.postRefreshToken().subscribe((token: any) => {
          let user = this.dataSync.current_user();
          user.accessToken = token.accessToken;
          user.refreshToken = token.refreshToken;
          this.dataSync.current_user.set(user);
          this.dataSync.updateUser(false, true).then(() => this.sendProfileUpdate())
        });
      }
    } else {
      this.sendProfileUpdate();
    }

  }


  /**
   * Format the profile data and send with {@link AuthService.postProfileUpdate}
   */
  sendProfileUpdate() {
    if (this.profileForm.valid) {
      let formValues = this.profileForm.value;
      let json = {
        "firstName": formValues.firstName,
        "lastName": formValues.lastName,
        "licenseNumber": formValues.licenseNum,
        "email": formValues.email,
        "phone": (formValues.phoneNum ?? '').toString(),
        "roles": [
          "User",
        ],
        "operatorUserId": this.dataSync.current_user().operatorUserID
      };

      this.auth.postProfileUpdate(json).subscribe(_ => {
        if (this.activeToast) this.activeToast.toastRef.close(); 
        this.activeToast = this.toastr.success('Profile updated', 'Success!');
        
        //Update local storage and current user
        this.dataSync.current_user.update(user => {
          user.firstName = formValues.firstName!;
          user.lastName = formValues.lastName!;
          user.email = formValues.email!;
          user.licenseNumber = formValues.licenseNum!;
          user.phoneNumber = (formValues.phoneNum ?? '').toString();
          return user;
        });

        this.dataSync.updateUser(true, false);
      }, err => {
        console.log(err);
        this.toastr.error(err.error.message ? err.error.message : err, 'Error');
      })
    }
  }


}
